import React, { useEffect, useState } from 'react';

const ProjectModal = ({ project, onClose, onSave, isEdit }) => {
  const [form, setForm] = useState({
    name: project?.name || '',
    clientId: project?.clientId || '',
    status: project?.status || 'pending',
  });
  const [loading, setLoading] = useState(false);
  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });
  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    await onSave(form);
    setLoading(false);
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)', padding: '1rem' }} onClick={onClose}>
      <div style={{ position: 'relative', padding: '2rem', background: 'var(--color-bg)', width: '100%', maxWidth: 480, margin: 'auto', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '1rem' }}>{isEdit ? 'Edit Project' : 'Add Project'}</h3>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Project Name</label>
            <input name="name" type="text" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.name} onChange={handleChange} required />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Client ID</label>
            <input name="clientId" type="text" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.clientId} onChange={handleChange} required />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 500, color: 'var(--color-text)', marginBottom: 4 }}>Status</label>
            <select name="status" style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', color: 'var(--color-text)', background: 'var(--color-bg)', fontSize: '1rem' }} value={form.status} onChange={handleChange}>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
            <button type="button" onClick={onClose} style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer' }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ background: 'var(--color-primary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.5rem 1.25rem', fontSize: '1rem', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>{loading ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Project')}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editProject, setEditProject] = useState(null);

  // Fetch projects and clients from backend
  const fetchProjects = async () => {
    setLoading(true);
    setError('');
    try {
      const [projectsRes, clientsRes] = await Promise.all([
        fetch('/api/get-projects.php'),
        fetch('/api/get-clients.php'),
      ]);
      const projectsData = await projectsRes.json();
      const clientsData = await clientsRes.json();
      setProjects(Array.isArray(projectsData) ? projectsData : (projectsData.projects || []));
      setClients(clientsData.clients || []);
    } catch (err) {
      setError('Failed to load projects.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Add project
  const handleAddProject = async (projectData) => {
    const res = await fetch('/api/add-project.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(projectData)
    });
    if (!res.ok) throw new Error('Failed to add project');
    await fetchProjects();
  };

  // Edit project
  const handleEditProject = async (editData) => {
    const res = await fetch('/api/edit-project.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: editProject.id, ...editData })
    });
    if (!res.ok) throw new Error('Failed to edit project');
    await fetchProjects();
  };

  // Delete project
  const handleDeleteProject = async (projectId) => {
    const res = await fetch('/api/delete-project.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: projectId })
    });
    if (!res.ok) throw new Error('Failed to delete project');
    await fetchProjects();
  };

  // Helper to get client name
  const getClientName = (clientId) => {
    const client = clients.find(c => String(c.id) === String(clientId));
    return client ? client.name : clientId;
  };

  return (
    <div style={{ padding: 'var(--section-padding)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--color-primary)', fontSize: '2rem', fontWeight: 700 }}>Projects</h2>
        <button
          style={{
            background: 'var(--color-primary)',
            color: 'var(--color-text-inverse)',
            border: 'none',
            borderRadius: 'var(--border-radius)',
            padding: '0.5rem 1.5rem',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: 'var(--box-shadow)'
          }}
          onClick={() => { setEditProject(null); setShowModal(true); }}
        >
          + Add Project
        </button>
      </div>
      {loading ? (
        <div>Loading projects...</div>
      ) : error ? (
        <div style={{ color: 'var(--color-error)' }}>{error}</div>
      ) : (
        <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--border-radius)', boxShadow: 'var(--box-shadow)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-bg-alt)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Client</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Status</th>
                <th style={{ padding: '1rem', textAlign: 'left', color: 'var(--color-text-light)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr><td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-light)' }}>No projects found.</td></tr>
              ) : (
                projects.map(project => (
                  <tr key={project.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{project.name}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{getClientName(project.clientId)}</td>
                    <td style={{ padding: '1rem', color: 'var(--color-text)' }}>{project.status}</td>
                    <td style={{ padding: '1rem' }}>
                      <button
                        style={{ background: 'var(--color-secondary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', marginRight: 8, cursor: 'pointer' }}
                        onClick={() => { setEditProject(project); setShowModal(true); }}
                      >
                        Edit
                      </button>
                      <button
                        style={{ background: 'var(--color-error)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--border-radius)', padding: '0.25rem 1rem', cursor: 'pointer' }}
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {showModal && (
        <ProjectModal
          project={editProject}
          isEdit={!!editProject}
          onClose={() => setShowModal(false)}
          onSave={editProject ? handleEditProject : handleAddProject}
        />
      )}
    </div>
  );
};

export default ProjectsPage; 